import { Modal } from 'react-bootstrap';
import { useState, type VoidFunctionComponent } from 'react';
import * as _ from 'lodash';
import Button, { BUTTON_SIZE, BUTTON_TYPE, LoaderButton } from '../../../generic/Button';
import { Icon, icons, LabeledTextInput } from '../../../generic';
import useCreateGitHubAppInstallation from './useCreateGitHubAppInstallation';
import Flex from '../../../generic/Flex';
import { useServiceStatus } from '../../../../hooks/api/services';
import { hardRedirectTo } from '../../../../utils';

const AddGitHubAppButton: VoidFunctionComponent = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [gitHubInstallationId, setGitHubInstallationId] = useState<string>('');
  const { error, isLoading, mutate: createGitHubApp, reset } = useCreateGitHubAppInstallation();
  const { data: serviceStatus, isLoading: isServiceStatusLoading } = useServiceStatus('github-app');
  const isUserConnectedToGitHub = !isServiceStatusLoading && serviceStatus?.isServiceConnected;

  const onOpen = () => setIsOpen(true);
  const onClose = () => {
    setGitHubInstallationId('');
    reset();
    setIsOpen(false);
  };

  const onSubmit = () => {
    createGitHubApp({ gitHubInstallationId }, { onSuccess: onClose });
  };
  const onConnectToGitHub = () => {
    // Redirect the user to GitHub's User OAuth web flow. Once complete, they'll
    // be directed back to the GitHub App settings page.
    hardRedirectTo('/api/services/github-app/authorize?next=/account/settings/integrations/github-app');
  };

  const isNotValid = _.isEmpty(gitHubInstallationId) || gitHubInstallationId.length < 2;

  return (
    <>
      <Button type={BUTTON_TYPE.SECONDARY} size={BUTTON_SIZE.MEDIUM} onClick={onOpen}>
        <Icon icon={icons.add} />
        Add GitHub App
      </Button>
      <Modal onHide={onClose} backdrop show={isOpen}>
        <Modal.Header closeButton>
          <Modal.Title>
            Add GitHub App Installation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Flex direction="column" gap="16px">
            {
              error &&
              <p className="alert alert-error">Error while creating installation: { error.message }</p>
            }
            {!isUserConnectedToGitHub && (
              <p className="alert">
                NOTE: This operation will prompt you for GitHub user authentication.
                This is required in order to verify that the FOSSA App is installed on GitHub.
              </p>
            )}
            {isUserConnectedToGitHub && (
              <LabeledTextInput
                label="Installation ID"
                placeholder="GitHub Installation ID"
                onChange={(v) => setGitHubInstallationId(v)}
                value={gitHubInstallationId}
                testId="gitHubInstallationId"
              />
            )}
          </Flex>
        </Modal.Body>
        <Modal.Footer>
          {isUserConnectedToGitHub ? (
            <LoaderButton
              isDisabled={isNotValid || isLoading}
              isLoading={isLoading}
              size={BUTTON_SIZE.MEDIUM}
              onClick={onSubmit}
              testId="submitAddInstallation"
            >
              Add Installation
            </LoaderButton>
          ) : (
            <LoaderButton
              isLoading={isServiceStatusLoading}
              size={BUTTON_SIZE.MEDIUM}
              onClick={onConnectToGitHub}
              testId="submitConnectToGitHub"
            >
              Connect to GitHub
            </LoaderButton>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddGitHubAppButton;
